import { useEffect, useState } from 'react';

const CACHE_KEY = 'fxrate:AUDUSD:v1';
const DEFAULT_BASE = 'AUD';
const DEFAULT_SYMBOL = 'USD';
const CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes – refetch after this to avoid stale rates

let inFlight = null;
let memCache = null; // { rate: number, date: string, fetchedAt?: number }

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isCacheFresh(payload) {
  if (!payload?.rate || !payload?.date) return false;
  const fetchedAt = payload.fetchedAt ?? 0;
  return Date.now() - fetchedAt < CACHE_MAX_AGE_MS;
}

async function fetchAudUsdRate() {
  if (memCache?.rate && memCache?.date && isCacheFresh(memCache)) return memCache;

  const stored = typeof window !== 'undefined' ? safeJsonParse(window.localStorage.getItem(CACHE_KEY)) : null;
  if (stored?.rate && stored?.date && isCacheFresh(stored)) {
    memCache = stored;
    return stored;
  }

  const fetchAndValidate = async (url) => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`FX rate fetch failed (${res.status})`);
    const data = await res.json();
    const rate = data?.rates?.USD;
    const date = data?.date;
    if (typeof rate !== 'number' || !Number.isFinite(rate) || !date) {
      throw new Error('FX rate response invalid');
    }
    return { rate, date, fetchedAt: Date.now() };
  };

  if (!inFlight) {
    inFlight = (async () => {
      // Primary: Netlify function (CDN-cached in production, works with netlify dev).
      // Fallback: Frankfurter directly (works when running CRA dev server without netlify dev).
      let payload;
      try {
        payload = await fetchAndValidate(
          `/.netlify/functions/fx-rate?base=${DEFAULT_BASE}&symbols=${DEFAULT_SYMBOL}`
        );
      } catch (e) {
        payload = await fetchAndValidate(
          `https://api.frankfurter.app/latest?from=${DEFAULT_BASE}&to=${DEFAULT_SYMBOL}`
        );
      }

        memCache = payload;
        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
        } catch {
          // ignore storage failures
        }
      return payload;
    })().finally(() => {
        inFlight = null;
      });
  }

  return inFlight;
}

export function useAudUsdFxRate() {
  const [rate, setRate] = useState(memCache?.rate ?? null);
  const [asOfDate, setAsOfDate] = useState(memCache?.date ?? null);
  const [loading, setLoading] = useState(!memCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchAudUsdRate()
      .then((payload) => {
        if (!mounted) return;
        setRate(payload.rate);
        setAsOfDate(payload.date);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return { rate, asOfDate, loading, error };
}


